import {MutationFunctionOptions} from "@apollo/client";
import {useCallback} from "react";
import {
    GetColumnsForTableDocument,
    TablesEnum,
    UpdateTableColumnSettingsMutation,
    UpdateTableColumnSettingsMutationVariables,
    useUpdateTableColumnSettingsMutation,
} from "../../generated/graphql";

/**
 * Zusätzliche, pro Aufruf erlaubte Mutation-Optionen (z.B. `onCompleted`,
 * `onError`, `awaitRefetchQueries`). `refetchQueries` ist hier bewusst
 * ausgeklammert: Das einheitliche Refetch wird von `usePersistColumns`
 * fest verdrahtet und darf von den Aufrufstellen nicht überschrieben werden.
 */
export type PersistColumnsMutationOptions = Omit<
    MutationFunctionOptions<UpdateTableColumnSettingsMutation, UpdateTableColumnSettingsMutationVariables>,
    "variables" | "refetchQueries"
>;

/** Rückgabesignatur der von `usePersistColumns` gelieferten Persistenz-Funktion. */
export type PersistColumnsFn = (
    table: TablesEnum,
    columns: Array<string>,
    mutationOptions?: PersistColumnsMutationOptions,
) => ReturnType<ReturnType<typeof useUpdateTableColumnSettingsMutation>[0]>;

/**
 * Einzige Persistenz-Naht für Spalten-Reihenfolge und -Sichtbarkeit.
 *
 * Kapselt `useUpdateTableColumnSettingsMutation` und ergänzt bei JEDEM Aufruf
 * dasselbe `refetchQueries` (die `GetColumnsForTable`-Query für die jeweilige
 * Tabelle). Dadurch laufen alle Stellen, die Spalten persistieren
 * (Spalten-Konfigurator: Hinzufügen/Entfernen/Verschieben, „Standard
 * wiederherstellen" und später Drag-and-Drop) über exakt eine Code-Stelle und
 * lösen konsistent denselben Cache-Refetch aus.
 *
 * WICHTIG: Wer Spalten speichert, soll NICHT direkt
 * `useUpdateTableColumnSettingsMutation` verwenden, sondern diesen Hook — sonst
 * fehlt das einheitliche `refetchQueries` (Befund „N7").
 *
 * @returns Eine stabile (`useCallback`) Funktion, die die Persistenz ausführt.
 *          Sie nimmt die Ziel-Tabelle (`tablesEnum`), die geordnete Liste der
 *          sichtbaren Spalten-Keys und optionale, pro Aufruf gültige
 *          Mutation-Optionen entgegen.
 */
export const usePersistColumns = (): PersistColumnsFn => {
    const [updateUserColumns] = useUpdateTableColumnSettingsMutation();

    return useCallback<PersistColumnsFn>(
        (table, columns, mutationOptions) =>
            updateUserColumns({
                ...mutationOptions,
                variables: {
                    updateTableColumnSettingsInput: {
                        table,
                        columns,
                    },
                },
                refetchQueries: [
                    "GetColumnsForTable",
                    {
                        query: GetColumnsForTableDocument,
                        variables: {table},
                    },
                ],
            }),
        [updateUserColumns],
    );
};
